import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { MessageSquareText } from 'lucide-react';
import { Tooltip } from '@wit-support/ui';
import { Time } from './time.js';
import { cn } from './lib/cn.js';
/** Сколько символов последнего комментария влезает в подсказку. */
export const COMMENT_PREVIEW_MAX_CHARS = 300;
/**
 * Тело комментария для подсказки: в одну строку и не длиннее предела.
 *
 * Переносы схлопываются раньше обрезки — иначе первые 300 символов могли бы
 * оказаться пустыми строками, и подсказка показала бы пустоту вместо текста.
 */
export function truncateCommentBody(body, max = COMMENT_PREVIEW_MAX_CHARS) {
    const flat = body.replace(/\s+/g, ' ').trim();
    return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}
/**
 * Маркер внутренних комментариев в строке диалога.
 *
 * НЕ ИНТЕРАКТИВЕН И НЕ ФОКУСИРУЕТСЯ намеренно: вся строка инбокса — это
 * ссылка. Кнопка внутри ссылки — невалидная вложенность, из-за которой клик
 * по маркеру перестал бы открывать диалог; свой `tabIndex` — лишняя остановка
 * табуляции внутри уже фокусируемой строки, по одной на каждый диалог с
 * комментариями. Поэтому `role="img"` с подписью — ровно та же форма, что у
 * соседних маркеров строки (упавший пайплайн, ожидание, алерты по платежам).
 *
 * Подсказка с последним комментарием открывается наведением на маркер И
 * фокусом строки (`rowFocused`): открытость управляемая — «наведено ИЛИ
 * строка в фокусе». Esc закрывает её до следующего фокуса строки.
 */
export function CommentIndicator({ count, last, rowFocused = false, descriptionId, className, }) {
    const [hovered, setHovered] = React.useState(false);
    const [dismissed, setDismissed] = React.useState(false);
    // Esc гасит подсказку только до тех пор, пока строка в фокусе; ушёл фокус —
    // следующий приход снова откроет её.
    React.useEffect(() => {
        if (!rowFocused)
            setDismissed(false);
    }, [rowFocused]);
    if (count <= 0)
        return null;
    const marker = (_jsxs("span", { role: "img", "aria-label": `Комментарии: ${count}`, className: cn('inline-flex shrink-0 items-center gap-0.5 text-[var(--color-text-muted)]', className), children: [_jsx(MessageSquareText, { className: "h-3.5 w-3.5", "aria-hidden": "true" }), _jsx("span", { className: "text-xs tabular-nums", children: count })] }));
    if (!last)
        return marker;
    const open = hovered || (rowFocused && !dismissed);
    return (_jsxs(_Fragment, { children: [_jsx(Tooltip.Provider, { children: _jsxs(Tooltip.Root, { open: open, onOpenChange: setHovered, children: [_jsx(Tooltip.Trigger, { asChild: true, children: marker }), _jsxs(Tooltip.Content, { className: "max-w-xs whitespace-normal", onEscapeKeyDown: () => setDismissed(true), children: [_jsx("span", { className: "font-medium", children: last.authorDisplayName }), ' · ', _jsx(Time, { iso: last.createdAt, preset: "timestamp" }), ' · ', truncateCommentBody(last.body)] })] }) }), descriptionId ? (_jsxs("span", { id: descriptionId, className: "sr-only", children: ["\u041F\u043E\u0441\u043B\u0435\u0434\u043D\u0438\u0439 \u043A\u043E\u043C\u043C\u0435\u043D\u0442\u0430\u0440\u0438\u0439: ", last.authorDisplayName, ",", ' ', _jsx(Time, { iso: last.createdAt, preset: "timestamp" }), ": ", truncateCommentBody(last.body)] })) : null] }));
}
CommentIndicator.displayName = 'CommentIndicator';
//# sourceMappingURL=comment-indicator.js.map